import * as React from 'react'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'
import { useQueryGpsSettings } from '@/lib/api/company/configuration/gps/useQueryGpsSettings'
import { useParams } from 'react-router-dom'
import { ConfigurationType } from '@/app/constants/configuration/configurationType'
import { QUERY_KEY_SETTINGS_GPS } from '@/lib/api/constants'
import { useMutationGpsSettings } from '@/lib/api/company/configuration/gps/useMutationGpsSettings'
import { GpsAndBorderCrossingConfigurationModel } from '@/lib/api/company/configuration/gps/types'
import { GpsForm } from './GpsForm'
import { GpsSkeleton } from './GpsSkeleton'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

// GPS data configuration page
export const GpsPageContent = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { t } = useLocalize()

  const diffLabels = React.useMemo<Record<string, string>>(() => ({
    countryAliasesWithMinWage: t('configuration-gps.country-aliases-min-wage'),
    restActivityAliases: t('configuration-gps.rest-activity-aliases'),
    drivingActivityAliases: t('configuration-gps.driving-activity-aliases'),
    tmsUnloadTime: t('configuration-gps.tms-unload-time'),
    showAutomaticBorderCrossings: t('configuration-gps.show-auto-border-crossings'),
    hideSameCountryCrossings: t('configuration-gps.hide-same-country-crossings'),
    hideSameCountryCrossingThresholdMin: t('configuration-gps.same-country-threshold'),
    showGpsCrossingsTogether: t('configuration-gps.show-gps-crossings-together'),
  }), [t])

  const query = useQueryGpsSettings(companyId)
  const mutation = useMutationGpsSettings(companyId)
  const lastQueryData =
    React.useRef<GpsAndBorderCrossingConfigurationModel | null>(null)

  const [state, setState] =
    React.useState<GpsAndBorderCrossingConfigurationModel | null>(null)

  React.useEffect(() => {
    const data = query.data
    if (!data) return
    setState((prev) => ({
      ...prev,
      ...data,
    }))
    lastQueryData.current = data
  }, [query.data])

  const isDirty = React.useMemo(() => {
    return JSON.stringify(lastQueryData.current) !== JSON.stringify(state)
  }, [state, lastQueryData])

  const handleReset = () => {
    setState((prev) => ({
      ...prev,
      ...lastQueryData.current,
    }))
  }

  const handleSave = () => {
    mutation.mutate(state)
  }

  return (
    <SettingsPageWrapper
      isReady={!!state}
      isPending={query.isFetching || mutation.isPending}
      isError={query.isError}
      handleSave={handleSave}
      handleReset={handleReset}
      restore={{
        configurationType: ConfigurationType.Gps,
        tabLabelKey: 'configuration-menu.gps',
        queryKeys: [QUERY_KEY_SETTINGS_GPS],
        serverData: query.data,
        withDefaults: true,
      }}
      isDirty={isDirty}
      errorMsg={t('configuration-gps.load-error')}
      globalErrorMsg={t('configuration-gps.load-error-global')}
      diffBefore={lastQueryData.current as Record<string, unknown>}
      diffAfter={state as Record<string, unknown>}
      diffLabels={diffLabels}
    >
      {state ? (<GpsForm isSaving={mutation.isPending} state={state} setState={setState}/>) : (<GpsSkeleton />)}
    </SettingsPageWrapper>
  )
}
